import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import {
    HttpContentWithData,
    HttpResponse,
    HttpError,
    RQAPICall,
    RQAPICallWithData,
} from 'store/APIClient';
import { STALE_TIME } from 'business/constants';
import { translateBlockValue } from 'i18n';
import {
    CommentsPerInscription,
    InscriptionT,
} from 'routes/BudgetModule/shared/entities/inscription';
import {
    BudgetProposalT,
    InscriptionWithMetadata,
} from 'routes/BudgetModule/config/types';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import {
    EditableBudgetPhases,
    getFieldForPhase,
    isActiveBudgetPhase,
} from 'routes/BudgetModule/shared/phases/helpers';
import { ColDef } from 'ag-grid-community';
import { Dispatch } from 'store/store';
import { transformUrl } from 'helpers/urlHelpers';
import { snakify } from 'helpers/syntaxHelper';
import { useTranslation } from 'react-i18next';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { AMOUNT, COMMENT, STATUS } from 'routes/BudgetModule/config/constants';

const PHASED_INSCRIPTION_FIELD = [COMMENT, AMOUNT, STATUS];

export const useGroupUpdateInscriptionStatus = (
    dispatch: FlowAnyObject,
    phaseObjectName: string,
    queryClient: FlowAnyObject,
    phaseInscriptionIdColumn: string,
    statusColumn: string,
    gridApi: FlowAnyObject,
    queryCacheKey: InscriptionQueryKey,
): FlowAnyFunction => {
    const { t } = useTranslation();

    const updateInscriptionMutation = useMutation<
        HttpResponse<HttpContentWithData<InscriptionT[]>>
    >({
        // @ts-expect-error Fix endpoint return type
        mutationFn: ({ ids, status }) => {
            const body = { ids, status };
            return RQAPICall({
                endpoint: `/budget_proposals/${phaseObjectName}/multi/status/`,
                body,
                method: 'PATCH',
                preProcessingUrlCallback: (url) =>
                    transformUrl(url, [2], snakify),
            });
        },

        // @ts-expect-error Fix endpoint return type
        onSuccess: (response, { ids, status }) => {
            dispatch.vision.notifyUser({
                message: t('budgetProposal.inscriptionStatusUpdated', {
                    count: response.content.data?.length || 0,
                }),
                snackType: 'success',
            });

            trackEventBudget(
                TRACKABLE_EVENTS.budget.general.changeStatusBatch,
                {
                    status: translateBlockValue(status),
                },
            );

            // @ts-expect-error Fix endpoint return type
            queryClient.setQueryData(queryCacheKey, (oldInscriptions) => {
                // @ts-expect-error Fix endpoint return type
                oldInscriptions.data.forEach((inscription) => {
                    if (ids.includes(inscription[phaseInscriptionIdColumn])) {
                        inscription[statusColumn] = status;
                    }
                });
                return oldInscriptions;
            });

            if (gridApi) gridApi.refreshCells();
        },
    });
    return updateInscriptionMutation;
};

export const useUpdateInscription = (
    dispatch: Dispatch,
    queryClient: FlowAnyObject,
    inscriptions: InscriptionWithMetadata[],
    queryCacheKey: InscriptionQueryKey,
): {
    updateInscriptionMutation: FlowAnyFunction;
} => {
    const updateInscriptionMutation = useMutation({
        mutationFn: ({
            id,
            phase,
            data: body,
        }: {
            id: number;
            phase: EditableBudgetPhases;
            data: Record<string, string | number | boolean | null>;
        }) =>
            RQAPICallWithData<BudgetProposalT[]>({
                endpoint: `budget_proposals/${getFieldForPhase(
                    phase,
                    'endpoint',
                )}/${id}`,
                body,
                method: 'PATCH',
                preProcessingUrlCallback: (url) =>
                    transformUrl(url, [1], snakify),
            }),

        onSuccess: (response, { id, phase }) => {
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });

            trackEventBudget(TRACKABLE_EVENTS.budget.general.editInscription, {
                editedField: Object.keys(response.content.data).find(
                    () => true,
                ),
            });
            const inscriptionIdFieldCamel = getFieldForPhase(phase, 'id');

            const currentInscription = inscriptions.find(
                (item) => item.inscription[inscriptionIdFieldCamel] === id,
            )?.inscription;

            const tableName = getFieldForPhase(phase, 'table');

            if (!isActiveBudgetPhase(phase)) {
                throw new Error(
                    'Unable to find table name of the budget proposal',
                );
            }

            const inscription = Object.keys(response.content.data).reduce(
                (output: InscriptionT, key) => {
                    const value = response.content.data[key];
                    const transformedKey = PHASED_INSCRIPTION_FIELD.includes(
                        key,
                    )
                        ? `${tableName}.${key as string}`
                        : key;

                    return {
                        ...output,
                        [transformedKey]: value,
                    };
                },
                {} as InscriptionT,
            );
            const row = {
                ...currentInscription,
                ...inscription,
            };

            // When we arrive there through Analyze, this cache might not exist
            queryClient.setQueryData(
                queryCacheKey,
                (oldInscriptions: {
                    data: InscriptionT[];
                    columns: ColDef[];
                }) => ({
                    ...oldInscriptions,
                    data: oldInscriptions?.data?.map((item) =>
                        item[inscriptionIdFieldCamel] === id ? row : item,
                    ),
                }),
            );
        },
        meta: {
            useGlobalOnError: true,
        },
    });
    return { updateInscriptionMutation };
};

export const useDeleteInscription = (
    cacheKey: InscriptionQueryKey,
    dispatch: FlowAnyObject,
    queryClient: FlowAnyObject,
    inscriptionId: string,
    inscriptionIdColumn?: string,
): FlowAnyFunction =>
    useMutation({
        mutationFn: () =>
            RQAPICall({
                endpoint: `inscriptions/${inscriptionId}`,
                method: 'DELETE',
            }),

        onSuccess: (response) => {
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });

            trackEventBudget(TRACKABLE_EVENTS.budget.general.deleteInscription);

            queryClient.setQueryData(
                cacheKey,
                // @ts-expect-error Fix endpoint return type
                (oldInscriptions) => ({
                    ...oldInscriptions,
                    data: oldInscriptions.data.filter(
                        // @ts-expect-error Fix endpoint return type
                        (ins) => ins[inscriptionIdColumn] !== inscriptionId,
                    ),
                }),
            );
        },

        meta: {
            useGlobalOnError: true,
        },
    });

export const useGetCommentPerInscriptions = (): UseQueryResult<
    CommentsPerInscription,
    HttpError
> =>
    useQuery<CommentsPerInscription, HttpError>({
        queryKey: ['comment_per_inscription'],

        queryFn: async () => {
            const response = await RQAPICallWithData<CommentsPerInscription>({
                endpoint: 'budget/inscription/all_comments',
                method: 'GET',
            });
            return response.content.data;
        },

        // 1 hour
        staleTime: STALE_TIME * 12,
    });
